import { useRef } from 'react';
import { TextInput, MultiSelect, Box, Title, Grid, Paper, Loader } from '@mantine/core';
import { IconMapPin, IconShield } from '@tabler/icons-react';
import { LocationProps } from './types';
import { useAddressSuggestions } from '../../hooks/useAddressSuggestions';

export function Location({ form, kontrollpunkte }: LocationProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    suggestions,
    isLoading,
    selectedIndex,
    error,
    handleKeyDown,
    handleSuggestionClick,
    handleBlur,
    handleFocus,
    handleChange
  } = useAddressSuggestions({
    inputValue: form.values.adresse,
    onSelect: (address) => {
      form.setFieldValue('adresse', address);
      inputRef.current?.focus();
    }
  });

  return (
    <Box>
      <Title order={3} mb="md" display="flex" style={{ alignItems: 'center', gap: '0.5rem' }}>
        <IconMapPin size={20} />
        Standortinformationen und Kontrollpunkte
      </Title>

      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Box pos="relative">
            <TextInput
              ref={inputRef}
              label="Ort"
              placeholder="Wählen Sie einen Bereich/Abteilung aus, um die Adresse anzuzeigen"
              description="Die Adresse wird automatisch aus dem gewählten Bereich übernommen. Änderungen über 'Bereiche' möglich."
              leftSection={<IconMapPin size={16} />}
              rightSection={isLoading ? <Loader size="xs" /> : null}
              error={error}
              {...form.getInputProps('adresse')}
              readOnly
              disabled={!form.values.adresse}
              styles={{
                input: {
                  cursor: form.values.adresse ? 'default' : 'not-allowed',
                  backgroundColor: form.values.adresse ? 'var(--mantine-color-gray-0)' : undefined
                }
              }}
            />
            {suggestions.length > 0 && (
              <Paper
                shadow="md"
                pos="absolute"
                top={0}
                left={0}
                right={0}
                mt={85}
                style={{ maxHeight: '240px', overflowY: 'auto', zIndex: 1000 }}
              >
                {suggestions.map((suggestion, index) => {
                  const textParts: { text: string; highlight: boolean }[] = [];
                  let lastIndex = 0;

                  // Sort highlights to ensure they're in order
                  const sortedHighlights = [...suggestion.highlights].sort((a, b) => a.start - b.start);

                  // Build text parts with highlights
                  sortedHighlights.forEach(({ start, end }) => {
                    if (start > lastIndex) {
                      textParts.push({
                        text: suggestion.display.slice(lastIndex, start),
                        highlight: false
                      });
                    }
                    textParts.push({
                      text: suggestion.display.slice(start, end),
                      highlight: true
                    });
                    lastIndex = end;
                  });

                  // Add remaining text
                  if (lastIndex < suggestion.display.length) {
                    textParts.push({
                      text: suggestion.display.slice(lastIndex),
                      highlight: false
                    });
                  }

                  return (
                    <Box
                      key={index}
                      p="xs"
                      onClick={() => handleSuggestionClick(suggestion)}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: index === selectedIndex ? 
                          'var(--mantine-color-blue-1)' : 
                          suggestion.isExactMatch ? 'var(--mantine-color-yellow-0)' :
                          'var(--mantine-color-white)',
                        transition: 'background-color 0.2s ease',
                      }}
                      data-hover
                    >
                      {textParts.map((part, i) => (
                        <span
                          key={i}
                          style={{
                            fontWeight: part.highlight ? 700 : 400,
                          }}
                        >
                          {part.text}
                        </span>
                      ))}
                    </Box>
                  );
                })}
              </Paper>
            )}
          </Box>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <MultiSelect
            label="Kontrollpunkte"
            placeholder="Wählen Sie Kontrollpunkte"
            data={kontrollpunkte}
            leftSection={<IconShield size={16} />}
            searchable
            {...form.getInputProps('kontrollpunkt')}
            required
          />
        </Grid.Col>
      </Grid>
    </Box>
  );
}
